import type React from 'react';
import { useState } from 'react';
import userPersonas from '@common/constants/userPersonas';
import StringConstants from '@common/constants/StringConstants';
import ConfirmStartPage from './modules/ConfirmStartPage';
import QuestionPage from './modules/QuestionPage';
import TryAgainPage from './modules/TryAgainPage';
import VerificationCompletePage from './modules/VerificationCompletePage';
import VerificationFailedPage from './modules/VerificationFailedPage';
import HandleErrorStatePage from './modules/HandleErrorStatePage';
import { ID_VERIFICATION_STATUS } from '@rental-manager/constants/OnboardingConstants';
import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import { Box, Button } from '@zillow/constellation-10';

interface Props {
  fromDashboard: boolean;
  requestIdentityVerificationQuestions: () => Record<string, any>;
  submitIdentityVerificationAnswers: (answers) => Record<string, any>;
  onIdentityVerificationSuccess: () => Record<string, unknown>;
  showRequestErrorNotification: () => Record<string, unknown>;
  onClose: () => void;
  onCancel: () => void;
  onBack: () => void;
  setupNewTerm: boolean;
  goToConfirmation: () => void;
  goToPrevPage: () => void;
  saveExperianErrorState: (args: string) => void;
}

type Question = {
  index: number;
  text: string;
  choices: Array<{ text: string; index: number; chosen: boolean }>;
  answered: boolean;
};

const KnowledgeBasedAuthenticationPage: React.FC<Props> = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [startAuthentication, setStartAuthentication] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questions, setQuestions] = useState<Array<Question>>([]);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const [showRetryPage, setShowRetryPage] = useState(false);
  const [showVerificationFailedPage, setShowVerificationFailedPage] = useState(false);
  const [showCompletePage, setShowCompletePage] = useState(false);
  const [showErrorHandlingPage, setShowErrorHandlingPage] = useState(false);

  const requestQuestions = async (): Promise<void> => {
    setIsLoading(true);
    setShowRetryPage(false);
    setShowVerificationFailedPage(false);
    setShowCompletePage(false);
    setShowErrorHandlingPage(false);
    const response = await props.requestIdentityVerificationQuestions();
    const { error, payload } = response?.action || {};
    if (error) {
      setIsLoading(false);
      if (
        payload?.response?.responseType &&
        (payload.response.responseType === ID_VERIFICATION_STATUS.USER_NOT_FOUND ||
          payload.response.responseType === ID_VERIFICATION_STATUS.FROZEN_CREDIT_REPORT ||
          payload.response.responseType === ID_VERIFICATION_STATUS.SSN_REQUIRED)
      ) {
        setShowErrorHandlingPage(true);
        props.saveExperianErrorState(payload.response.responseType);
      } else {
        props.showRequestErrorNotification();
      }
      return;
    }
    setIsLoading(false);
    setStartAuthentication(true);
    setCurrentQuestionIndex(0);
    setQuestions(
      response.questionSet.map((question, questionIndex) => ({
        index: questionIndex,
        text: question.questionText,
        choices: question.questionChoices.map((choice, choiceIndex) => ({
          text: choice,
          index: choiceIndex + 1,
          chosen: false,
        })),
        answered: false,
      })),
    );
  };

  const continueToNextQuestion = () => {
    setCurrentQuestionIndex((prev) => prev + 1);
  };

  const backToPreviousQuestion = () => {
    setCurrentQuestionIndex((prev) => prev - 1);
  };

  const chooseAnswer = (chosenIndex) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((question, index) =>
        currentQuestionIndex === index
          ? {
              ...question,
              choices: question.choices.map((choice) => ({
                ...choice,
                chosen: chosenIndex === choice.index,
              })),
              answered: true,
            }
          : question,
      ),
    );
  };

  const submitAnswer = () => {
    if (currentQuestionIndex === questions.length - 1) {
      submitAnswers();
    } else {
      continueToNextQuestion();
    }
  };

  const submitAnswers = async (): Promise<void> => {
    setIsLoading(true);
    const answerChosen = (choice) => choice.chosen;
    const answerIndex = (question) => question.choices.filter(answerChosen)[0].index;
    const answers = questions.map(answerIndex);
    const response = await props.submitIdentityVerificationAnswers(answers);
    if (response.success) {
      onIdentityVerificationSuccess();
      return;
    }
    if (response.responseType === 'INCORRECT_ANSWERS' && response.remainingAttempts > 0) {
      setIsLoading(false);
      setShowRetryPage(true);
      setRemainingAttempts(response.remainingAttempts);
    } else {
      onIdentityVerificationUnrecoverableFailure();
    }
  };

  const onIdentityVerificationSuccess = () => {
    goToVerificationCompletePage();
  };

  const onIdentityVerificationUnrecoverableFailure = () => {
    setIsLoading(false);
    setShowVerificationFailedPage(true);
  };

  const goToVerificationCompletePage = () => {
    setIsLoading(false);
    setShowCompletePage(true);
  };

  const onBack = () => {
    if (!startAuthentication) {
      if (props.setupNewTerm) {
        props.goToPrevPage();
      } else {
        props.onBack();
      }
    } else if (currentQuestionIndex !== 0) {
      backToPreviousQuestion();
    }
  };

  const onClose = () => {
    props.onClose();
  };

  const handleIdentityVerificationSuccess = async (): Promise<any> => {
    setIsLoading(true);
    const result = await props.onIdentityVerificationSuccess();
    if (result && result.success && props.setupNewTerm) {
      setIsLoading(false);
      props.goToConfirmation();
    }
  };

  const renderModalBody = () => {
    if (showErrorHandlingPage) {
      return <HandleErrorStatePage />;
    }
    if (showCompletePage) {
      return <VerificationCompletePage />;
    }
    if (showRetryPage) {
      return <TryAgainPage remainingAttempts={remainingAttempts} />;
    }
    if (showVerificationFailedPage) {
      return <VerificationFailedPage />;
    }
    if (!startAuthentication) {
      return <ConfirmStartPage />;
    }
    const currentQuestion = questions[currentQuestionIndex];
    return <QuestionPage question={currentQuestion} chooseAnswer={chooseAnswer} />;
  };

  const renderFooterCTA = () => {
    if (showErrorHandlingPage || showCompletePage) {
      return (
        <Button emphasis="filled" onClick={handleIdentityVerificationSuccess} loading={isLoading}>
          Continue
        </Button>
      );
    }
    if (showRetryPage) {
      return (
        <Button className="mtm" emphasis="filled" onClick={requestQuestions} loading={isLoading}>
          Try again
        </Button>
      );
    }
    if (showVerificationFailedPage) {
      return (
        <Button emphasis="filled" asChild>
          <a href={StringConstants.LINKS.SUPPORT_PAGE.LANDLORD} target="_blank" rel="noopener noreferrer">
            Contact customer support
          </a>
        </Button>
      );
    }
    if (!startAuthentication) {
      return (
        <Button emphasis="filled" onClick={requestQuestions} loading={isLoading}>
          Continue
        </Button>
      );
    }
    const currentQuestion = questions[currentQuestionIndex];
    return (
      <Button emphasis="filled" onClick={submitAnswer} disabled={!currentQuestion.answered} loading={isLoading}>
        Continue
      </Button>
    );
  };

  const isOnFirstQuestionScreen = startAuthentication && currentQuestionIndex === 0;
  const isOnConfirmStartScreenFromDashboard = props.fromDashboard && !startAuthentication;
  const hideBackButton =
    isOnFirstQuestionScreen ||
    isOnConfirmStartScreenFromDashboard ||
    showRetryPage ||
    showCompletePage ||
    showVerificationFailedPage ||
    showErrorHandlingPage ||
    isLoading;

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={onClose}
      header={props.fromDashboard ? 'Verification' : 'Payment setup'}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          {renderModalBody()}
        </Box>
      }
      footerEnd={renderFooterCTA()}
    />
  );
};

KnowledgeBasedAuthenticationPage.defaultProps = {
  fromDashboard: false,
  setupNewTerm: false,
};

export default KnowledgeBasedAuthenticationPage;
